import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { testable } from '@instructure/ui-testable';
import { themeable } from '@instructure/ui-themeable';
import { controllable, Children as ChildrenPropTypes } from '@instructure/ui-prop-types';
import { omitProps, safeCloneElement } from '@instructure/ui-react-utils';
import { IconMoveStartLine } from '@instructure/ui-icons';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { NavigationItem } from "./NavigationItem/index.js";
var styles = {
  componentId: 'dNJkF',
  template: function template(theme) {
    return "\n\n.dNJkF_bGBk{background-color:".concat(theme.backgroundColor || 'inherit', ";box-sizing:border-box;color:").concat(theme.fontColor || 'inherit', ";display:flex;flex-direction:column;height:100%;overflow-y:auto;width:").concat(theme.width || 'inherit', "}\n\n.dNJkF_bGBk.dNJkF_GARE{width:").concat(theme.minimizedWidth || 'inherit', "}\n\n.dNJkF_bGBk.dNJkF_GARE>.dNJkF_cjSw{transform:rotateY(-180deg)}\n\n.dNJkF_caGd{flex:1 0 auto;list-style-type:none}\n\n.dNJkF_caGd,.dNJkF_cpmC{margin:0;padding:0}\n\n.dNJkF_cjSw{transform:translateZ(0);transition:all ").concat(theme.toggleTransition || 'inherit', "}\n\n.dNJkF_daxS{fill:").concat(theme.fill || 'inherit', ";margin:0 auto}");
  },
  'root': 'dNJkF_bGBk',
  'minimized': 'dNJkF_GARE',
  'toggle': 'dNJkF_cjSw',
  'content': 'dNJkF_caGd',
  'list': 'dNJkF_cpmC',
  'toggleIcon': 'dNJkF_daxS'
};
import theme from "./theme.js";

var navMinimized = function navMinimized(_ref) {
  var minimized = _ref.minimized;
  return {
    minimized: !minimized
  };
};
/**
---
category: components
---
**/


var Navigation = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Navigation, _Component);

  var _super = _createSuper(Navigation);

  function Navigation(props) {
    var _this;

    _classCallCheck(this, Navigation);

    _this = _super.call(this);

    _this.handleNavToggle = function (event) {
      if (!_this.isControlled()) {
        _this.setState(navMinimized);
      }

      _this.props.onMinimized(event, !_this.minimized);
    };

    _this.state = {
      minimized: _this.isControlled(props) ? props.minimized : !!props.defaultMinimized
    };
    return _this;
  }

  _createClass(Navigation, [{
    key: "isControlled",
    value: function isControlled() {
      var props = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : this.props;
      return typeof props.minimized === 'boolean';
    }
  }, {
    key: "renderChildren",
    value: function renderChildren() {
      var _this2 = this;

      return Children.map(this.props.children, function (child) {
        var navItem = safeCloneElement(child, {
          minimized: _this2.state.minimized
        });
        return /*#__PURE__*/React.createElement("li", {
          className: styles.list
        }, navItem);
      });
    }
  }, {
    key: "toggleMessage",
    value: function toggleMessage() {
      return this.state.minimized ? this.props.toggleLabel.minimizedLabel : this.props.toggleLabel.expandedLabel;
    }
  }, {
    key: "render",
    value: function render() {
      var _classnames;

      var label = this.props.label;
      var props = omitProps(this.props, Navigation.propTypes, ['minimized']);
      var navClasses = classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles.minimized, this.minimized), _classnames));
      return /*#__PURE__*/React.createElement("nav", Object.assign({}, props, {
        className: navClasses,
        "aria-label": label
      }), /*#__PURE__*/React.createElement("ul", {
        className: styles.content
      }, this.renderChildren()), /*#__PURE__*/React.createElement("div", {
        className: styles.toggle
      }, /*#__PURE__*/React.createElement(NavigationItem, {
        "aria-expanded": !this.minimized,
        onClick: this.handleNavToggle,
        icon: /*#__PURE__*/React.createElement(IconMoveStartLine, {
          className: styles.toggleIcon,
          inline: false
        }),
        label: /*#__PURE__*/React.createElement(ScreenReaderContent, null, this.toggleMessage())
      })));
    }
  }, {
    key: "minimized",
    get: function get() {
      if (this.isControlled()) {
        return !!this.props.minimized;
      }

      return !!this.state.minimized;
    }
  }]);

  Navigation.displayName = "Navigation";
  return Navigation;
}(Component), _class2.propTypes = {
  /**
   * children of type Navigation.Item
   */
  children: ChildrenPropTypes.oneOf([NavigationItem]),

  /**
   * When minimized is set to true, the `<Navigation />` shows icons only while the text becomes a tooltip. When it is set to false, the `<Navigation />` shows text in addition to the icons
   */
  minimized: controllable(PropTypes.bool, 'onMinimized', 'defaultMinimized'),

  /**
   * Whether the `<Navigation />` is initially minimized (uncontrolled)
   */
  defaultMinimized: PropTypes.bool,
  onMinimized: PropTypes.func,

  /**
   * Screen reader label for the main Navigation
   */
  label: PropTypes.string.isRequired,

  /**
   * Screen reader label for the toggle button expanded/minimized state
   */
  toggleLabel: PropTypes.shape({
    expandedLabel: PropTypes.string,
    minimizedLabel: PropTypes.string
  }).isRequired,

  /**
   * If the `<Navigation.Item>` goes to a new page, pass an href
   */
  href: PropTypes.string,

  /**
   * If the `<Navigation.Item>` does not go to a new page pass an onClick
   */
  onClick: PropTypes.func
}, _class2.defaultProps = {
  children: null,
  defaultMinimized: false,
  onMinimized: function onMinimized(event, minimized) {},
  onClick: function onClick(e) {},
  href: void 0,
  minimized: void 0
}, _class2.Item = NavigationItem, _temp)) || _class) || _class);
export default Navigation;
export { Navigation, NavigationItem };